import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { ACTIVE_BUSINESS_HEADER } from '../axiosConfig';
import { getValidToken } from '../utils/auth';
import {
  getStoredActiveBusinessId,
  resolveInitialActiveBusiness,
  storeActiveBusinessId,
} from '../utils/businessContext';

export const ACTIVE_BUSINESS_CHANGED_EVENT = 'haastia.activeBusinessChanged';
export const AUTH_SESSION_CHANGED_EVENT = 'haastia.authSessionChanged';

const EMPTY_CONTEXT = Object.freeze({
  activeBusinessId: null,
  membership: null,
  business: null,
  payload: {},
  hydrated: false,
});

// `activeBusinessId === null` used to mean three different things at once:
// still bootstrapping, the bootstrap failed, and the account genuinely has no
// workspace. Callers could not tell them apart, so a failed /api/auth/me
// rendered as "select a business workspace" on a page with nothing to select
// and no way to retry. Derive an explicit state instead of overloading null.
export const WORKSPACE_STATE = Object.freeze({
  LOADING: 'loading',
  UNAVAILABLE: 'unavailable',
  EMPTY: 'empty',
  READY: 'ready',
});

export const deriveWorkspaceState = ({ hydrated, bootstrapError, activeBusinessId }) => {
  if (!hydrated) return WORKSPACE_STATE.LOADING;
  if (bootstrapError) return WORKSPACE_STATE.UNAVAILABLE;
  if (activeBusinessId) return WORKSPACE_STATE.READY;
  return WORKSPACE_STATE.EMPTY;
};

const ActiveBusinessContext = createContext(null);
const sameId = (left, right) => String(left || '') === String(right || '');

const buildContext = (payload = {}, preferredBusinessId = null) => {
  const businesses = Array.isArray(payload.businesses) ? payload.businesses : [];
  const preferredBusiness = preferredBusinessId
    ? businesses.find((item) => sameId(item?._id, preferredBusinessId))
    : null;
  const business = preferredBusiness || resolveInitialActiveBusiness(payload) || null;
  const activeBusinessId = business?._id || null;
  const memberships = payload.businessMemberships || payload.memberships || [];
  const membership = memberships.find((item) =>
    sameId(item.business || item.businessId, activeBusinessId));
  return { activeBusinessId, membership: membership || null, business, payload };
};

const readTokenContext = () => {
  const payload = getValidToken()?.payload || {};
  const preferredBusinessId = getStoredActiveBusinessId()
    || payload.activeBusiness?._id
    || payload.businesses?.[0]?._id
    || null;
  const context = buildContext(payload, preferredBusinessId);
  // The full business object is refreshed from /api/auth/me. Retaining a
  // previously selected id during that brief hydration window keeps scoped
  // requests stable without treating token claims as the permission source.
  return context.activeBusinessId || !preferredBusinessId
    ? context
    : { ...context, activeBusinessId: preferredBusinessId };
};

export const emitActiveBusinessChanged = (activeBusinessId, { internal = false } = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ACTIVE_BUSINESS_CHANGED_EVENT, {
    detail: { activeBusinessId, internal },
  }));
};

// Nothing renders until this settles: `hydrated` gates renderWorkspaceRoutes in
// AppRoutes, and the visual-editor shell has no navbar to escape with. A request
// that never comes back would leave a spinner forever, so bound it — a timeout
// lands in refresh()'s catch, which sets `hydrated` and a retryable error.
const WORKSPACE_BOOTSTRAP_TIMEOUT_MS = 20000;

const loadAuthenticatedProfile = async ({ token, preferredBusinessId, signal }) => {
  const request = (businessId) => axios.get('/api/auth/me', {
    headers: {
      Authorization: `Bearer ${token}`,
      ...(businessId ? { [ACTIVE_BUSINESS_HEADER]: businessId } : {}),
    },
    signal,
    timeout: WORKSPACE_BOOTSTRAP_TIMEOUT_MS,
    skipActiveBusinessHeader: true,
  });

  try {
    return await request(preferredBusinessId);
  } catch (error) {
    if (!preferredBusinessId || error?.code === 'ERR_CANCELED' || signal?.aborted) throw error;
    // A 400/403/404 is the server telling us this workspace id is not usable by
    // this account, so drop it. Any other failure (500, timeout, network) says
    // nothing about the id's validity -- retry without the header anyway, since
    // the header is the only part of the request we can vary, but keep the
    // stored selection so a transient blip does not lose the user's workspace.
    if ([400, 403, 404].includes(error?.response?.status)) {
      storeActiveBusinessId(null);
    }
    return request(null);
  }
};

export const ActiveBusinessProvider = ({ children }) => {
  const [context, setContext] = useState(() => ({ ...EMPTY_CONTEXT, ...readTokenContext() }));
  const [pendingBusinessId, setPendingBusinessId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');
  const [bootstrapError, setBootstrapError] = useState(null);
  const [workspaceVersion, setWorkspaceVersion] = useState(0);
  const mountedRef = useRef(true);
  const refreshControllerRef = useRef(null);
  const switchControllerRef = useRef(null);
  const switchSequenceRef = useRef(0);
  const workspaceControllerRef = useRef(new AbortController());
  const dirtySourcesRef = useRef(new Map());
  const activeBusinessIdRef = useRef(context.activeBusinessId);

  activeBusinessIdRef.current = context.activeBusinessId;

  const replaceWorkspaceController = useCallback(() => {
    workspaceControllerRef.current.abort();
    workspaceControllerRef.current = new AbortController();
    setWorkspaceVersion((current) => current + 1);
  }, []);

  const registerUnsavedChanges = useCallback((sourceId, isDirty, message = '') => {
    if (!sourceId) return () => {};
    if (isDirty) dirtySourcesRef.current.set(sourceId, message);
    else dirtySourcesRef.current.delete(sourceId);
    return () => dirtySourcesRef.current.delete(sourceId);
  }, []);

  // Read at the moment someone tries to leave (UnsavedChangesGuard), so it is a
  // getter rather than state: registering never re-renders the app.
  const hasUnsavedChanges = useCallback(() => dirtySourcesRef.current.size > 0, []);

  const refresh = useCallback(async ({ preferredBusinessId = null } = {}) => {
    switchControllerRef.current?.abort();
    switchSequenceRef.current += 1;
    refreshControllerRef.current?.abort();
    const controller = new AbortController();
    refreshControllerRef.current = controller;
    const auth = getValidToken();
    const tokenContext = readTokenContext();
    if (mountedRef.current) {
      setPendingBusinessId(null);
      setSwitching(false);
    }

    if (!auth?.token) {
      storeActiveBusinessId(null);
      if (mountedRef.current) {
        replaceWorkspaceController();
        setBootstrapError(null);
        setContext({ ...EMPTY_CONTEXT, hydrated: true });
      }
      return null;
    }

    const selectedId = preferredBusinessId
      || getStoredActiveBusinessId()
      || tokenContext.activeBusinessId;
    try {
      const response = await loadAuthenticatedProfile({
        token: auth.token,
        preferredBusinessId: selectedId,
        signal: controller.signal,
      });
      if (!mountedRef.current || controller.signal.aborted) return null;
      const nextContext = buildContext(response?.data?.user || {}, selectedId);
      storeActiveBusinessId(nextContext.activeBusinessId);
      replaceWorkspaceController();
      setBootstrapError(null);
      setContext({ ...nextContext, hydrated: true });
      setSwitchError('');
      if (!sameId(activeBusinessIdRef.current, nextContext.activeBusinessId)) {
        emitActiveBusinessChanged(nextContext.activeBusinessId, { internal: true });
      }
      return nextContext;
    } catch (error) {
      if (!mountedRef.current || controller.signal.aborted || error?.code === 'ERR_CANCELED') return null;
      setContext((current) => ({ ...current, hydrated: true }));
      setBootstrapError({
        code: 'BOOTSTRAP_FAILED',
        status: error?.response?.status ?? null,
      });
      return null;
    }
  }, [replaceWorkspaceController]);

  const switchBusiness = useCallback(async (nextBusinessId, { force = false } = {}) => {
    const normalizedId = String(nextBusinessId || '').trim();
    if (!normalizedId) return false;
    if (sameId(normalizedId, activeBusinessIdRef.current)) return true;

    const knownBusinesses = Array.isArray(context.payload?.businesses)
      ? context.payload.businesses
      : [];
    if (knownBusinesses.length && !knownBusinesses.some((business) => sameId(business?._id, normalizedId))) {
      setSwitchError('That workspace is no longer available to this account.');
      return false;
    }

    if (!force && dirtySourcesRef.current.size > 0 && typeof window !== 'undefined') {
      const customMessage = Array.from(dirtySourcesRef.current.values()).find(Boolean);
      if (!window.confirm(customMessage || 'You have unsaved changes. Switch workspaces and discard them?')) {
        return false;
      }
    }

    const auth = getValidToken();
    if (!auth?.token) {
      setSwitchError('Your session has expired. Please sign in again.');
      return false;
    }

    setSwitchError('');
    setPendingBusinessId(normalizedId);
    setSwitching(true);
    replaceWorkspaceController();
    switchControllerRef.current?.abort();
    const controller = new AbortController();
    switchControllerRef.current = controller;
    const switchSequence = switchSequenceRef.current + 1;
    switchSequenceRef.current = switchSequence;

    try {
      const response = await axios.post('/api/auth/active-business', {
        activeBusinessId: normalizedId,
      }, {
        headers: { Authorization: `Bearer ${auth.token}` },
        signal: controller.signal,
        skipActiveBusinessHeader: true,
      });
      if (!mountedRef.current || controller.signal.aborted || switchSequenceRef.current !== switchSequence) {
        return false;
      }
      const nextContext = buildContext(response?.data?.user || {}, normalizedId);
      if (!sameId(nextContext.activeBusinessId, normalizedId) || !nextContext.membership) {
        throw new Error('The selected workspace could not be resolved.');
      }

      dirtySourcesRef.current.clear();
      storeActiveBusinessId(normalizedId);
      setContext({ ...nextContext, hydrated: true });
      setPendingBusinessId(null);
      setSwitching(false);
      replaceWorkspaceController();
      emitActiveBusinessChanged(normalizedId, { internal: true });
      return true;
    } catch (error) {
      if (
        !mountedRef.current ||
        controller.signal.aborted ||
        error?.code === 'ERR_CANCELED' ||
        switchSequenceRef.current !== switchSequence
      ) return false;
      setPendingBusinessId(null);
      setSwitching(false);
      setSwitchError(
        error?.response?.data?.error?.message
          || error?.response?.data?.error
          || error?.message
          || 'Unable to switch workspaces. Please try again.',
      );
      replaceWorkspaceController();
      return false;
    }
  }, [context.payload?.businesses, replaceWorkspaceController]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
      refreshControllerRef.current?.abort();
      switchControllerRef.current?.abort();
      workspaceControllerRef.current.abort();
    };
  }, [refresh]);

  useEffect(() => {
    const handleAuthChange = () => refresh();
    const handleLegacyBusinessChange = (event) => {
      if (event?.detail?.internal) return;
      const nextBusinessId = event?.detail?.activeBusinessId;
      if (nextBusinessId && !sameId(nextBusinessId, activeBusinessIdRef.current)) {
        switchBusiness(nextBusinessId, { force: true });
      }
    };
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, handleAuthChange);
    window.addEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, handleLegacyBusinessChange);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, handleAuthChange);
      window.removeEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, handleLegacyBusinessChange);
    };
  }, [refresh, switchBusiness]);

  const value = useMemo(() => ({
    ...context,
    businesses: Array.isArray(context.payload?.businesses) ? context.payload.businesses : [],
    memberships: context.payload?.businessMemberships || context.payload?.memberships || [],
    providerId: context.membership?.provider || null,
    capabilities: context.business?.viewerCapabilities || null,
    pendingBusinessId,
    switching,
    switchError,
    bootstrapError,
    workspaceState: deriveWorkspaceState({
      hydrated: context.hydrated,
      bootstrapError,
      activeBusinessId: context.activeBusinessId,
    }),
    workspaceVersion,
    workspaceSignal: workspaceControllerRef.current.signal,
    refresh,
    switchBusiness,
    registerUnsavedChanges,
    hasUnsavedChanges,
  }), [bootstrapError, context, hasUnsavedChanges, pendingBusinessId, refresh, registerUnsavedChanges, switchBusiness, switchError, switching, workspaceVersion]);

  return <ActiveBusinessContext.Provider value={value}>{children}</ActiveBusinessContext.Provider>;
};

export const useActiveBusiness = () => {
  const context = useContext(ActiveBusinessContext);
  const [fallbackSelection, setFallbackSelection] = useState({ activeBusinessId: null, version: 0 });
  useEffect(() => {
    if (context || typeof window === 'undefined') return undefined;
    const selectFallback = (event) => setFallbackSelection((current) => ({
      activeBusinessId: event?.detail?.activeBusinessId || null,
      version: current.version + 1,
    }));
    const refreshFallback = () => setFallbackSelection((current) => ({
      activeBusinessId: null,
      version: current.version + 1,
    }));
    window.addEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, selectFallback);
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, refreshFallback);
    return () => {
      window.removeEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, selectFallback);
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, refreshFallback);
    };
  }, [context]);
  const fallback = useMemo(() => {
    const tokenContext = readTokenContext();
    const selectedId = fallbackSelection.activeBusinessId;
    const selectedContext = selectedId
      ? buildContext(tokenContext.payload, selectedId)
      : tokenContext;
    const resolvedContext = selectedId && !selectedContext.activeBusinessId
      ? { ...selectedContext, activeBusinessId: selectedId }
      : selectedContext;
    return {
      ...EMPTY_CONTEXT,
      ...resolvedContext,
      hydrated: true,
      businesses: Array.isArray(resolvedContext.payload?.businesses)
        ? resolvedContext.payload.businesses
        : [],
      memberships:
        resolvedContext.payload?.businessMemberships
        || resolvedContext.payload?.memberships
        || [],
      providerId: resolvedContext.membership?.provider || null,
      capabilities: resolvedContext.business?.viewerCapabilities || null,
      pendingBusinessId: null,
      switching: false,
      switchError: '',
      bootstrapError: null,
      workspaceState: deriveWorkspaceState({
        hydrated: true,
        bootstrapError: null,
        activeBusinessId: resolvedContext.activeBusinessId,
      }),
      workspaceVersion: 0,
      workspaceSignal: undefined,
      refresh: async () => resolvedContext,
      switchBusiness: async () => false,
      registerUnsavedChanges: () => () => {},
      hasUnsavedChanges: () => false,
    };
  }, [fallbackSelection]);

  // Keeping a token-derived fallback makes leaf components and isolated tests
  // backward compatible while App supplies the authoritative provider in normal use.
  return context || fallback;
};

export default useActiveBusiness;
